import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMAIL, WHATS_APP } from 'constants/messageMediums';
import { getDataFeeds } from 'modules/templatePicker/ducks/selectors';
import { noop } from 'utils/noop';

import { TemplateSource } from 'constants/templateTypes';
import { CampaignType } from 'modules/domains/campaign';
import { useSendersExist } from '../hooks/useSendersExist';
import { EmptyTileState } from './EmptyTileState';
import { TemplateTilesListItem } from './TemplateTilesListItem';
import { StyledUl } from './Ul.styled';

type TemplateTilesProps = {
  campaignType?: CampaignType;
  messageMedium?: string;
  onSelectTemplate: (template: any) => void;
  onSelectDataFeed: (dataFeedId: number | null) => void;
  query: string;
  selectedTab: TemplateSource;
  templateId?: number; // selected template id
  templates: any[];
  totalNumberOfTemplates: number;
  hasError?: boolean;
  useDataFeeds?: boolean;
};

export const TemplateTiles = ({
  campaignType,
  messageMedium = EMAIL,
  onSelectTemplate = noop,
  onSelectDataFeed = noop,
  query,
  selectedTab,
  templateId,
  templates = [],
  totalNumberOfTemplates,
  hasError = false,
  useDataFeeds = false,
}: TemplateTilesProps) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateId);

  const handleSelect = useCallback(
    (template: any) => {
      onSelectTemplate(template);
      setSelectedTemplateId(template.id);
    },
    [onSelectTemplate],
  );

  const dataFeeds = useSelector(getDataFeeds);

  // custom hook that figures out whether or not the project
  // has sms senders and authed emails.
  // without them, a project cannot create their respective template.
  const { smsSendersExist, authedEmailsExist } = useSendersExist();

  const showEmptyTileState = !totalNumberOfTemplates;

  if (showEmptyTileState) {
    return (
      <EmptyTileState
        authedEmailsExist={authedEmailsExist}
        messageMedium={messageMedium}
        query={query}
        smsSendersExist={smsSendersExist}
        selectedTab={selectedTab}
      />
    );
  }
  return (
    <StyledUl
      aria-labelledby={selectedTab}
      data-test="template-create-wizard-tile-section"
      role="tabpanel"
    >
      {templates.map((template) => (
        <TemplateTilesListItem
          campaignType={campaignType}
          isSelected={template.id === selectedTemplateId}
          key={`${template.id}-${template.name}`}
          messageMedium={messageMedium}
          onClick={() => handleSelect(template)}
          onFocus={(e) => {
            if (messageMedium === WHATS_APP && !e.target.id.startsWith('template-tile')) return;
            handleSelect(template);
          }}
          searchText={query}
          templateId={template.id}
          hasError={hasError}
          dataFeeds={useDataFeeds ? dataFeeds : []}
          onSelectDataFeed={onSelectDataFeed}
          templateType={template.templateType}
          {...template}
        />
      ))}
    </StyledUl>
  );
};
